import React from "react"
import { Col, Flex, Row, Tag, Typography } from "antd"
import {
  CheckCircleOutlined,
  ShoppingOutlined,
  TeamOutlined,
  GlobalOutlined,
  TrophyOutlined,
  InfoCircleOutlined
} from "@ant-design/icons"
import PropTypes from "prop-types"
import { useWizardContext } from "../../../WizardContext"
import { TRIP_ITEMS, SKILL_LEVELS, TRIP_LANGUAGES } from "constants/experiences"
import styles from "./PreviewDetails.module.css"

const { Title, Text, Paragraph } = Typography

const itemLabelMap = Object.fromEntries((TRIP_ITEMS || []).map((i) => [i.value, i.name]))
const skillLabelMap = Object.fromEntries((SKILL_LEVELS || []).map((s) => [s.value, s.label]))
const langLabelMap = Object.fromEntries((TRIP_LANGUAGES || []).map((l) => [l.value, l.label]))

const getCapacityText = (min, max) => {
  if (min != null && max != null) return `${min}–${max} guests`
  if (min != null) return `Min ${min} guests`
  return `Max ${max} guests`
}

const buildAmenities = (accessibilityEnabled, childrenAllowed, alcoholAllowed) => {
  const amenities = []
  if (accessibilityEnabled) amenities.push({ label: "Wheelchair Accessible", color: "green" })
  if (childrenAllowed) amenities.push({ label: "Child Friendly", color: "cyan" })
  if (alcoholAllowed) amenities.push({ label: "Alcohol Allowed", color: "purple" })
  return amenities
}

const ItemChip = ({ label }) => (
  <Flex align="center" gap="small" className={styles.itemChip}>
    <CheckCircleOutlined className={styles.chipIcon} />
    <Text className={styles.chipLabel}>{label}</Text>
  </Flex>
)

ItemChip.propTypes = { label: PropTypes.string.isRequired }

export const PreviewDetails = () => {
  const { form } = useWizardContext()
  const description = form.getFieldValue("description")
  const minGuests = form.getFieldValue("min_guests")
  const maxGuests = form.getFieldValue("max_guests")
  const languages = form.getFieldValue("languages") || []
  const skillLevels = form.getFieldValue("skill_levels") || []
  const includedItems = form.getFieldValue("included_items") || []
  const bringItems = form.getFieldValue("bring_items") || []
  const accessibilityEnabled = form.getFieldValue("accessibility_enabled")
  const alcoholAllowed = form.getFieldValue("alcohol_allowed")
  const childrenAllowed = form.getFieldValue("children_allowed")

  const hasCapacity = minGuests != null || maxGuests != null
  const amenities = buildAmenities(accessibilityEnabled, childrenAllowed, alcoholAllowed)

  const hasData =
    description ||
    includedItems.length > 0 ||
    bringItems.length > 0 ||
    skillLevels.length > 0 ||
    languages.length > 0 ||
    hasCapacity

  if (!hasData) return null

  return (
    <div className={styles.container}>
      <Title level={3} className={styles.title}>{"About"}</Title>

      {description && (
        <Paragraph className={styles.description}>{description}</Paragraph>
      )}

      {hasCapacity && (
        <Flex align="center" gap="small" className={styles.capacityRow}>
          <span className={styles.metaIconWrap}>
            <TeamOutlined className={styles.metaIcon} />
          </span>
          <Text className={styles.capacityLabel}>{"Capacity:"}</Text>
          <Text strong className={styles.capacityValue}>{getCapacityText(minGuests, maxGuests)}</Text>
        </Flex>
      )}

      {includedItems.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <CheckCircleOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"What's Included"}</Text>
          </Flex>
          <Row gutter={[8, 8]}>
            {includedItems.map((item) => (
              <Col xs={24} sm={12} key={item}>
                <ItemChip label={itemLabelMap[item] ?? item} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {bringItems.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <ShoppingOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"What to Bring"}</Text>
          </Flex>
          <Row gutter={[8, 8]}>
            {bringItems.map((item) => (
              <Col xs={24} sm={12} key={item}>
                <ItemChip label={itemLabelMap[item] ?? item} />
              </Col>
            ))}
          </Row>
        </div>
      )}

      {(languages.length > 0 || skillLevels.length > 0) && (
        <div className={styles.section}>
          <Row gutter={[12, 12]}>
            {languages.length > 0 && (
              <Col xs={24} sm={12}>
                <Flex align="center" gap="small" className={styles.sectionHeader}>
                  <GlobalOutlined className={styles.sectionIcon} />
                  <Text strong className={styles.sectionTitle}>{"Languages"}</Text>
                </Flex>
                <Flex wrap="wrap" gap="small">
                  {languages.map((l) => (
                    <Tag key={l} color="blue" className={styles.tag}>{langLabelMap[l] ?? l}</Tag>
                  ))}
                </Flex>
              </Col>
            )}
            {skillLevels.length > 0 && (
              <Col xs={24} sm={12}>
                <Flex align="center" gap="small" className={styles.sectionHeader}>
                  <TrophyOutlined className={styles.sectionIcon} />
                  <Text strong className={styles.sectionTitle}>{"Skill Levels"}</Text>
                </Flex>
                <Flex wrap="wrap" gap="small">
                  {skillLevels.map((s) => (
                    <Tag key={s} color="gold" className={styles.tag}>{skillLabelMap[s] ?? s}</Tag>
                  ))}
                </Flex>
              </Col>
            )}
          </Row>
        </div>
      )}

      {amenities.length > 0 && (
        <div className={styles.section}>
          <Flex align="center" gap="small" className={styles.sectionHeader}>
            <InfoCircleOutlined className={styles.sectionIcon} />
            <Text strong className={styles.sectionTitle}>{"Additional Info"}</Text>
          </Flex>
          <Flex wrap="wrap" gap="small">
            {amenities.map((a) => (
              <Tag key={a.label} color={a.color} className={styles.tag}>{a.label}</Tag>
            ))}
          </Flex>
        </div>
      )}
    </div>
  )
}
